/**
 * The organization anagrafica form — CREATE from the list, EDIT from the detail page.
 *
 * It was born inside `PlatformOrganizationsPage`; it moved out when the detail became its
 * own route, because two screens now open it and a second copy of a fiscal form is how the
 * two would start asking for different fields.
 */
import { useMutation } from "@tanstack/react-query";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  type PlatformOrganization,
  type PlatformOrganizationIn,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";

/** The editable anagrafica, as strings — blank means "unset" and the API normalises it. */
interface FormState {
  name: string;
  legal_name: string;
  vat_number: string;
  tax_code: string;
  email: string;
  phone: string;
  country: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  legal_name: "",
  vat_number: "",
  tax_code: "",
  email: "",
  phone: "",
  country: "",
  notes: "",
};

function toForm(c: PlatformOrganization): FormState {
  return {
    name: c.name,
    legal_name: c.legal_name ?? "",
    vat_number: c.vat_number ?? "",
    tax_code: c.tax_code ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    country: c.country ?? "",
    notes: c.notes ?? "",
  };
}

/** Blank → null so "unset" reaches the API with ONE representation, never `""`. */
function toBody(form: FormState): PlatformOrganizationIn {
  const clean = (v: string) => (v.trim() ? v.trim() : null);
  return {
    name: form.name.trim(),
    legal_name: clean(form.legal_name),
    vat_number: clean(form.vat_number),
    tax_code: clean(form.tax_code),
    email: clean(form.email),
    phone: clean(form.phone),
    country: clean(form.country),
    notes: clean(form.notes),
  };
}

export function OrganizationDialog({
  organization,
  onClose,
  onSaved,
}: {
  organization: PlatformOrganization | null; // null = create
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = React.useState<FormState>(
    organization ? toForm(organization) : EMPTY_FORM,
  );
  const save = useMutation({
    mutationFn: async () =>
      organization
        ? platformApi.updateOrganization(organization.id, toBody(form))
        : platformApi.createOrganization(toBody(form)),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const field = (key: keyof FormState, label: string, placeholder?: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={`org-${key}`}>{label}</Label>
      <Input
        id={`org-${key}`}
        value={form[key]}
        onChange={(e) => set({ [key]: e.target.value } as Partial<FormState>)}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {organization ? `Modifica — ${organization.name}` : "Nuova organizzazione"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {field("name", "Nome", "Acme SpA")}
          {field("legal_name", "Ragione sociale", "Acme Società per Azioni")}
          <div className="grid grid-cols-2 gap-3">
            {field("vat_number", "Partita IVA", "IT01234567890")}
            {field("tax_code", "Codice fiscale")}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {field("email", "Email di riferimento", "amministrazione@acme.it")}
            {field("phone", "Telefono")}
          </div>
          {field("country", "Paese (ISO 2)", "IT")}
          {field("notes", "Note")}
          {save.isError && (
            <p className="text-sm text-danger">
              {save.error instanceof PlatformApiError ? save.error.message : "Salvataggio fallito"}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={() => save.mutate()} disabled={!form.name.trim() || save.isPending}>
            {save.isPending ? "Salvo…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
